// Bootstrap do Painel Tráfego VG (ES modules + Supabase).
//
// Fluxo:
//   auth → (ao logar) store.load() + realtime → renderArea()
//   + wire da navegação, contadores da sidebar, export/import e logout.
//
// Toda a persistência vive nas telas (ui/*) via repositories; aqui só
// orquestramos. SEM localStorage, SEM seeds: a fonte da verdade é o Supabase.
import { initAuth, logout } from './auth.js'
import {
  state,
  load,
  subscribeRealtime,
  unsubscribeRealtime,
  clear,
  onRealtime
} from './store.js'
import { toast, ligarFechamentoDropdowns } from './ui/shared.js'
import { onSaveStatus, temPendencias } from './lib/save-status.js'
import {
  ofertasRepo,
  criativosRepo,
  contasRepo,
  perfisRepo,
  proxysRepo,
  diariasRepo
} from './data/repositories.js'

import * as ofertasUI from './ui/ofertas.js'
import * as contasUI from './ui/contas.js'
import * as perfisUI from './ui/perfis.js'
import * as proxysUI from './ui/proxys.js'
import * as gestoresUI from './ui/gestores.js'

// Área atualmente visível.
let area = 'ofertas'

// Injeta o toast compartilhado nas telas (evita acoplamento direto).
ofertasUI.setToast(toast)
contasUI.setToast(toast)
perfisUI.setToast(toast)
proxysUI.setToast(toast)
gestoresUI.setToast(toast)

/* ============ Navegação ============ */
function renderArea() {
  if (area === 'ofertas') ofertasUI.render()
  else if (area === 'contas') contasUI.renderContas()
  else if (area === 'perfis') perfisUI.renderPerfis()
  else if (area === 'proxys') proxysUI.renderProxys()
  else if (area === 'gestores') gestoresUI.renderGestores()
  pintarIndicadores()
}

function ligarNav() {
  document.querySelectorAll('.nav').forEach(n => {
    n.onclick = () => {
      area = n.dataset.area
      document.querySelectorAll('.nav').forEach(x => x.classList.toggle('active', x === n))
      ;['ofertas', 'contas', 'perfis', 'proxys', 'gestores'].forEach(a =>
        document.getElementById('area-' + a).classList.toggle('hidden', a !== area)
      )
      renderArea()
    }
  })
}

/* ============ Contadores da sidebar ============ */
function refreshCounts() {
  document.getElementById('cntContas').textContent = state.contas.length
  document.getElementById('cntPerfis').textContent = state.perfis.length
  document.getElementById('cntProxys').textContent = state.proxys.length
  const dc = document.getElementById('cntDemandas')
  if (dc) dc.textContent = gestoresUI.totalDemandasPendentes()
}

/* ============ Export / Import ============ */
// Exporta o estado atual como JSON (mesmo formato do app legado).
function ligarExport() {
  document.getElementById('expBtn').onclick = () => {
    const dump = {
      ofertas: state.ofertas.map(o => ({
        id: o.codigo,
        gestor: o.gestor,
        data: o.data,
        ticket: o.ticket,
        ic: o.ic,
        cpa: o.cpa,
        rows: o.criativos.map(c => [
          c.data,
          c.conta,
          c.status,
          c.criativo,
          c.gestor,
          c.gasto,
          c.ic,
          c.vendas
        ])
      })),
      contas: state.contas.map(c => [
        c.conta,
        c.bm,
        c.tipo,
        c.offer,
        c.gestor,
        c.situacao,
        c.qualidade,
        c.status,
        c.data,
        c.observacoes
      ]),
      perfis: state.perfis.map(p => [
        p.data,
        p.squad,
        p.gestor,
        p.login_fb,
        p.email,
        p.senha_fb,
        p.senha_email,
        p.fa_2,
        p.conta_recuperacao,
        p.links
      ]),
      proxys: state.proxys.map(p => [
        p.nome,
        [p.host, p.porta, p.usuario, p.senha].filter(v => String(v ?? '').trim() !== '').join(':'),
        p.gestor,
        p.status
      ]),
      diarias: state.diarias.map(d => ({ ...d }))
    }
    const blob = new Blob([JSON.stringify(dump, null, 2)], { type: 'application/json' })
    const a = document.createElement('a')
    a.href = URL.createObjectURL(blob)
    a.download = 'painel-trafego-' + new Date().toISOString().slice(0, 10) + '.json'
    a.click()
    toast('Exportado')
  }
}

// Importa um JSON e faz BULK-INSERT no Supabase, depois recarrega o app.
// Aceita o formato legado (arrays posicionais / objetos de oferta).
function ligarImport() {
  document.getElementById('impBtn').onclick = () => document.getElementById('fileInput').click()
  document.getElementById('fileInput').onchange = e => {
    const f = e.target.files[0]
    if (!f) return
    const r = new FileReader()
    r.onload = async () => {
      let d
      try {
        d = JSON.parse(r.result)
        if (!d || typeof d !== 'object') throw 0
      } catch (err) {
        toast('Arquivo inválido')
        return
      }
      const ok = confirm(
        'Importar vai SUBSTITUIR os dados atuais (ofertas, contas, perfis, ' +
          'proxys e tarefas) pelos do arquivo. Esta ação não tem desfazer. Continuar?'
      )
      if (!ok) return
      try {
        await importarParaSupabase(d)
        await recarregar()
        toast('Dados importados')
      } catch (err) {
        toast('Falha na importação', true)
      }
    }
    r.readAsText(f)
    e.target.value = ''
  }
}

// Insere objetos em lotes (evita um único insert gigante).
async function inserirEmLotes(repo, objs, tam = 200) {
  for (let i = 0; i < objs.length; i += tam) {
    await repo.insertMany(objs.slice(i, i + tam))
  }
}

// SUBSTITUI os dados pelos do dump legado: para cada seção presente, limpa a
// tabela e recarrega. Assim importar o mesmo arquivo 2x não duplica nada.
async function importarParaSupabase(d) {
  // OFERTAS + CRIATIVOS — clearAll de ofertas remove os criativos por cascade.
  if (Array.isArray(d.ofertas)) {
    await ofertasRepo.clearAll()
    let pos = 0
    for (const o of d.ofertas) {
      const salva = await ofertasRepo.insert({
        codigo: o.id || '',
        gestor: o.gestor || '',
        data: o.data || '',
        ticket: o.ticket ?? 0,
        ic: o.ic ?? 0,
        cpa: o.cpa ?? 0,
        posicao: pos++
      })
      const rows = Array.isArray(o.rows) ? o.rows : []
      await criativosRepo.insertMany(
        rows.map((row, ci) => ({
          oferta_id: salva.id,
          data: row[0] || '',
          conta: row[1] || '',
          status: row[2] || 'ATIVA',
          criativo: row[3] || '',
          gestor: row[4] || '',
          gasto: row[5] ?? 0,
          ic: row[6] ?? 0,
          vendas: row[7] ?? 0,
          posicao: ci
        }))
      )
    }
  }
  // CONTAS
  if (Array.isArray(d.contas)) {
    await contasRepo.clearAll()
    await inserirEmLotes(
      contasRepo,
      d.contas.map((r, i) => ({
        conta: r[0] || '',
        bm: r[1] || '',
        tipo: r[2] || 'Normal',
        offer: r[3] || '',
        gestor: r[4] || '',
        situacao: r[5] || '',
        qualidade: r[6] || '',
        status: r[7] || 'ATIVO',
        data: r[8] || '',
        observacoes: r[9] || '',
        posicao: i
      }))
    )
  }
  // PERFIS
  if (Array.isArray(d.perfis)) {
    await perfisRepo.clearAll()
    await inserirEmLotes(
      perfisRepo,
      d.perfis.map((r, i) => ({
        data: r[0] || '',
        squad: r[1] || '',
        gestor: r[2] || '',
        login_fb: r[3] || '',
        email: r[4] || '',
        senha_fb: r[5] || '',
        senha_email: r[6] || '',
        fa_2: r[7] || '',
        conta_recuperacao: r[8] || '',
        links: r[9] || '',
        posicao: i
      }))
    )
  }
  // PROXYS
  if (Array.isArray(d.proxys)) {
    await proxysRepo.clearAll()
    await inserirEmLotes(
      proxysRepo,
      d.proxys.map((r, i) => {
        const parts = String(r[1] || '').split(':')
        return {
          nome: r[0] || '',
          host: parts[0] || '',
          porta: parts[1] || '',
          usuario: parts[2] || '',
          senha: parts[3] || '',
          gestor: r[2] || '',
          status: r[3] || 'Ativa',
          posicao: i
        }
      })
    )
  }
  // DIARIAS
  if (Array.isArray(d.diarias)) {
    await diariasRepo.clearAll()
    await inserirEmLotes(
      diariasRepo,
      d.diarias.filter(t => t && !Array.isArray(t)).map((t, i) => ({ ...t, posicao: i }))
    )
  }
}

/* ============ Logout ============ */
// Injeta um botão de logout no rodapé da sidebar e o liga.
function ligarLogout() {
  const foot = document.querySelector('.side-foot')
  if (!foot || document.getElementById('logoutBtn')) return
  const btn = document.createElement('button')
  btn.className = 'btn-sm'
  btn.id = 'logoutBtn'
  btn.innerHTML =
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4"/><path d="M16 17l5-5-5-5M21 12H9"/></svg> Sair'
  btn.onclick = async () => {
    unsubscribeRealtime()
    clear()
    await logout()
  }
  foot.appendChild(btn)
}

/* ============ Carga / recarga do app ============ */
async function recarregar() {
  await load()
  renderArea()
  refreshCounts()
}

// Re-render pendente de uma mudança vinda do realtime enquanto o usuário edita.
let rerenderPendente = false

function renderAtualExterno() {
  if (area === 'ofertas') ofertasUI.onExternalChange()
  else if (area === 'contas') contasUI.onExternalChange()
  else if (area === 'perfis') perfisUI.onExternalChange()
  else if (area === 'proxys') proxysUI.onExternalChange()
  else if (area === 'gestores') gestoresUI.onExternalChange()
}

// Chamado pelo realtime: re-renderiza só a área afetada e atualiza contadores.
// Se o usuário está com um campo em foco na área, ADIA o re-render até o foco
// sair da área (evita perder digitação por uma mudança de outro cliente).
function aoMudarRemoto(areaAfetada) {
  if (areaAfetada === area) {
    const ae = document.activeElement
    const editando =
      ae && /^(INPUT|SELECT|TEXTAREA)$/.test(ae.tagName) && ae.closest('#area-' + area)
    if (editando) rerenderPendente = true
    else renderAtualExterno()
  }
  refreshCounts()
}

// Aplica um re-render adiado quando o foco finalmente sai da área em edição.
function instalarReRenderAdiavel() {
  document.querySelectorAll('main section[id^="area-"]').forEach(sec => {
    sec.addEventListener('focusout', () => {
      setTimeout(() => {
        if (!rerenderPendente) return
        const ae = document.activeElement
        if (ae && ae.closest('#area-' + area)) return // ainda editando na área
        rerenderPendente = false
        renderAtualExterno()
        refreshCounts()
      }, 0)
    })
  })
}

/* ============ Boot ============ */
async function aoLogar() {
  await load()
  subscribeRealtime()
  renderArea()
  refreshCounts()
}

// Indicador de salvamento no topo de cada tabela (thead-bar): Tudo salvo /
// Salvando… / Erro ao salvar.
const conteudoSalvar = {
  ok: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M5 13l4 4L19 7"/></svg>Tudo salvo',
  salvando:
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 12a9 9 0 1 1-6.2-8.5"/></svg>Salvando…',
  erro: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 9v4m0 4h.01M10.3 3.9l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.7-3l-8-14a2 2 0 0 0-3.4 0z"/></svg>Erro ao salvar'
}
let estadoSalvar = 'ok'

// Garante um indicador atualizado no topo de cada tabela visível.
function pintarIndicadores() {
  document.querySelectorAll('.thead-bar').forEach(bar => {
    let el = bar.querySelector(':scope > .save-status')
    if (!el) {
      el = document.createElement('span')
      el.className = 'save-status'
      const search = bar.querySelector('.search')
      if (search) bar.insertBefore(el, search)
      else bar.appendChild(el)
    }
    el.className = 'save-status ' + estadoSalvar
    el.innerHTML = conteudoSalvar[estadoSalvar]
  })
}

function instalarIndicadorSalvamento() {
  onSaveStatus(estado => {
    estadoSalvar = estado
    pintarIndicadores()
  })
}

// Avisa antes de fechar/recarregar a aba se houver algo não salvo.
function instalarAvisoSaida() {
  window.addEventListener('beforeunload', e => {
    if (temPendencias()) {
      e.preventDefault()
      e.returnValue = ''
    }
  })
}

function init() {
  ligarNav()
  ofertasUI.ligarNovaOferta()
  contasUI.ligarContas()
  perfisUI.ligarPerfis()
  proxysUI.ligarProxys()
  gestoresUI.ligarGestores()
  ligarExport()
  ligarImport()
  ligarLogout()
  ligarFechamentoDropdowns()
  instalarReRenderAdiavel()
  instalarIndicadorSalvamento()
  instalarAvisoSaida()
  onRealtime(aoMudarRemoto)
  initAuth(aoLogar)
}

init()
